import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import get from 'lodash.get';
import { useSessionStorage } from '~/modules/common/hooks/useSessionStorage';

export const TASK_RESOURCE_ESTIMATES_LIST_QUERY = gql`
  query getTaskResourceEstimatesForTask(
    $taskId: String!
    $page: Int!
    $pageSize: Int!
    $includeTaskResourceUserCostProgressSummary: Boolean!
  ) {
    task(taskId: $taskId) {
      id
      resourceEstimates(page: $page, pageSize: $pageSize) {
        id
        taskResourceEstimateId
        taskId
        resourceUser {
          id
          uri
          slug
          displayText
        }
        projectRole {
          id
          displayText
        }
        initialEstimatedHours
        initialEstimatedCost {
          amount
          currency {
            symbol
          }
        }
        taskResourceUserAllocation {
          id
          taskUri
          projectUri
          roleUri
          startDate
          endDate
          scheduleRules {
            dateRange {
              startDate
              endDate
            }
            do
          }
          totalHours
        }
        taskResourceUserHoursProgressSummary {
          actualHours
          estimatedAtCompletionHours
          estimatedRemainingHours
        }
        taskResourceUserCostProgressSummary
          @include(if: $includeTaskResourceUserCostProgressSummary) {
          actualCost {
            amount
            currency {
              symbol
            }
          }
          estimatedAtCompletionCost {
            amount
            currency {
              symbol
            }
          }
          estimatedRemainingCost {
            amount
            currency {
              symbol
            }
          }
        }
      }
    }
  }
`;

export const DEFAULT_PAGE_SIZE = 10;

export const updateTaskResourceEstimateEntryInTaskResourceEstimatesListQueryCache = (
  proxy,
  variables
) => updatedTaskResourceEstimateFragment => {
  try {
    const cachedData = proxy.readQuery({
      query: TASK_RESOURCE_ESTIMATES_LIST_QUERY,
      variables
    });

    if (cachedData?.task?.resourceEstimates) {
      const updatedEstimates = cachedData.task.resourceEstimates.map(est =>
        est.id === updatedTaskResourceEstimateFragment.id
          ? { ...est, ...updatedTaskResourceEstimateFragment }
          : est
      );

      proxy.writeQuery({
        query: TASK_RESOURCE_ESTIMATES_LIST_QUERY,
        variables,
        data: {
          task: {
            ...cachedData.task,
            resourceEstimates: updatedEstimates
          }
        }
      });
    }
    // eslint-disable-next-line no-empty
  } catch (_err) {}
};

export const useTaskResourceEstimates = ({
  taskId,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  includeTaskResourceUserCostProgressSummary = false
}) => {
  const { setValue: setVariables } = useSessionStorage(
    'get-task-resource-estimates-for-task-variables',
    null
  );

  const variables = {
    taskId,
    page: page || 1,
    pageSize,
    includeTaskResourceUserCostProgressSummary
  };

  const { data, loading, refetch } = useQuery(
    TASK_RESOURCE_ESTIMATES_LIST_QUERY,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        setVariables(variables);
      }
    }
  );

  return {
    loading,
    resourceEstimates: get(data, 'task.resourceEstimates', []),
    refetch
  };
};

export default useTaskResourceEstimates;
